import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useCountdownTimer } from '../../../hooks/useCountdownTimer';
import { postRequest } from '../../../services/api';
import Button from '../../buttons/Button';
import type { WizardChildProps } from '../../Wizard/types';

import { FIFTEEN_MINUTES_IN_SECONDS, REDEEM_RIDDLE_CHALLENGE_PERSISTENCE_KEY } from './constants';

import './RedeemDialog.scss';

const RedeemRiddleDialog = (props: WizardChildProps) => {
  const [answer, setAnswer] = useState('');
  const { t } = useTranslation();
  const [isWrongAnswer, setIsWrongAnswer] = useState(false);
  const [attempt, setAttempt] = useState(1);
  const [UniqueFormulas, setUniqueFormulas] = useState(0);

  const [timeLeft, startCountdown] = useCountdownTimer(
    FIFTEEN_MINUTES_IN_SECONDS,
    REDEEM_RIDDLE_CHALLENGE_PERSISTENCE_KEY,
  );

  const [firstNumber, secondNumber, thirdNumber, fourthNumber, fifthNumber] = useMemo(() => {
    let firstNumberVal, secondNumberVal, thirdNumberVal, fourthNumberVal, fifthNumberVal;
    let result;

    do {
      firstNumberVal = Math.floor(Math.random() * 20) + 10;
      secondNumberVal = Math.floor(Math.random() * 20) + 10;
      thirdNumberVal = Math.floor(Math.random() * 10) + 10;
      fourthNumberVal = Math.floor(Math.random() * 50) + 10;
      fifthNumberVal = Math.floor(Math.random() * 50) + 10;

      result = (firstNumberVal * secondNumberVal) / thirdNumberVal + fourthNumberVal - fifthNumberVal;
    } while (!Number.isInteger(result) || result <= 0); // Repeat until the result is an integer

    return [firstNumberVal, secondNumberVal, thirdNumberVal, fourthNumberVal, fifthNumberVal];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [UniqueFormulas]);

  const sendRedeemChallenge = async (status: 'COMPLETED' | 'FAILED') => {
    await postRequest('redeem/challenge', {
      type: 'RIDDLE',
      attempt,
      status,
      challenge: `${firstNumber} * ${secondNumber} / ${thirdNumber} + ${fourthNumber} - ${fifthNumber}`,
      answer,
    });
  };

  const resetValues = () => {
    setAnswer('');
    setIsWrongAnswer(false);
    setAttempt(1);
    setUniqueFormulas((prev) => prev + 1);
  };

  const onAnswer = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsWrongAnswer(false);

    let isSuccess = false;

    const correctAnswer = (firstNumber * secondNumber) / thirdNumber + fourthNumber - fifthNumber;
    const userAnswer = Number(answer);

    if (userAnswer === correctAnswer) {
      if (props.isCurrent) {
        props.nextStep();
      }
      isSuccess = true;
      resetValues();
    } else if (attempt < 3) {
      setAnswer('');
      setIsWrongAnswer(true);
      setAttempt((prev) => prev + 1);
    } else {
      startCountdown();
      resetValues();
    }

    void sendRedeemChallenge(isSuccess ? 'COMPLETED' : 'FAILED'); // no need to wait for the response
  };

  const isEnabled = !timeLeft;

  return (
    <div className="redeem__riddle_dialog">
      <div className="redeem__riddle_dialog_title">{t('redeem.riddle.title')}</div>
      <form className="redeem__riddle_dialog_form" onSubmit={onAnswer}>
        <div className="redeem__riddle_dialog_content">
          {isEnabled ? (
            <>
              <div className="redeem__riddle_dialog_text_top">{t('redeem.riddle.lawText')}</div>
              <div className="redeem__riddle_dialog_text_bottom">
                <span className="redeem__riddle_dialog_text_bottom_calculate">{t('redeem.riddle.calculate')}</span>
                <span className="redeem__riddle_dialog_text_bottom_question">
                  {t('redeem.riddle.formula', { firstNumber, secondNumber, thirdNumber, fourthNumber, fifthNumber })}
                </span>
              </div>
              <input
                className="redeem__riddle_dialog_input"
                placeholder={isWrongAnswer ? t('redeem.riddle.wrongAnswer') : t('redeem.riddle.typeAnswer')}
                style={{ border: isWrongAnswer ? '1px solid red' : undefined }}
                type="text"
                value={answer}
                onChange={(e) => {
                  if (!isNaN(Number(e.target.value)) && e.target.value.length <= 3) setAnswer(e.target.value);
                }}
              />
            </>
          ) : (
            <div className="redeem__riddle_dialog_text_top">
              <span>{t('redeem.riddle.tooManyMistakes', { timeLeft })}</span>
            </div>
          )}
        </div>

        <div className="redeem__riddle_dialog_btn">
          {isEnabled ? (
            <Button btnType="main" className="redeem__riddle_dialog_answer_btn" disabled={!answer} type="submit">
              {t('common.answer')}
            </Button>
          ) : (
            <Button btnType="main" className="redeem__riddle_dialog_answer_btn" onClick={props.prevStep}>
              {t('common.back')}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default RedeemRiddleDialog;
